/* -----------------Types--------------- */
import type { Confidence } from "@memorang/types";

import { AI_SUBMITTABLE_VARIANTS } from "@features/session/constants";
import useAIFeedbackStore from "@features/session/hooks/useAIFeedbackStore";
import { useAppTheme } from "@hooks/useAppTheme";
import type {
	Choice,
	OriginalItemTypes,
	OriginalStimulusTypes,
	RenderableItemTypes,
	SubskillSchema,
} from "@memorang/types/src/session/new-session";
import { Box } from "@memorang/ui";
import type { BoxProps } from "@memorang/ui/src/components/Box";
import { useTranslation } from "react-i18next";
/* -----------------Third parties--------------- */
import * as Animatable from "react-native-animatable";
import ExplanationContainer from "../../../containers/ExplanationContainer";
import AiModalHandle from "../AiModalHandle";
/* -----------------Components--------------- */
import AiPostAnswerHeader from "../AiPostAnswerHeader";

type Props = {
	choices: Choice[];
	timeTaken: number;
	maximumScore: number;
	confidence?: Confidence;
	correct?: boolean;
	currentQuestionAnswered?: boolean;
	showExplanation?: boolean;
	tags?: string[];
	showStats?: boolean;
	pointsEarnedStatValue?: string;
	itemType: RenderableItemTypes;
	itemId: number;
	numberOfWords?: number;
	originalItemType?: OriginalItemTypes;
	stimulusType?: OriginalStimulusTypes;
	subskillsSchema?: SubskillSchema[];
};

const BorderWrapper = ({ children, ...props }: BoxProps) => {
	const theme = useAppTheme();
	return (
		<Box
			borderWidth={2}
			padding={16}
			borderColor={theme.colors.divider}
			borderRadius={theme.roundness * 4}
			gap={20}
			overflow="hidden"
			{...props}
		>
			{children}
		</Box>
	);
};
const PostAnswerModal = ({
	choices,
	// timeTaken,
	maximumScore,
	confidence,
	correct,
	currentQuestionAnswered,
	showExplanation,
	// tags,
	pointsEarnedStatValue,
	itemId,
	itemType,
	// numberOfWords,
	originalItemType,
	stimulusType,
	subskillsSchema,
}: Props) => {
	const { t } = useTranslation();

	const isAiItemType = AI_SUBMITTABLE_VARIANTS.includes(itemType);

	const score = useAIFeedbackStore(
		(store) => store.aiResponse?.[itemId]?.score,
	);
	const subskillAssessments = useAIFeedbackStore(
		(store) => store.aiResponse?.[itemId]?.subskillAssessments,
	);

	const aiResponse = useAIFeedbackStore((state) => state.aiResponse);
	const currentItemAiResponse = aiResponse?.[itemId.toString()];

	if (itemType === "EXPLORE_ACCORDION") {
		return null;
	}

	return (
		<Animatable.View
			useNativeDriver
			duration={300}
			animation="fadeIn"
			easing="ease-out"
		>
			{currentQuestionAnswered ? (
				<Box gap={16}>
					{/* <BorderWrapper>
						<PostAnswerStats
							timeTaken={timeTaken}
							correct={correct}
							isAiItemType={isAiItemType}
							confidence={confidence}
							numAnsweredStat={
								correct
									? t("session.answer.correct")
									: t("session.answer.incorrect")
							}
							pointsEarnedStatValue={pointsEarnedStatValue}
							numberOfWords={numberOfWords}
							itemType={itemType}
							stimulusType={stimulusType}
						/>
					</BorderWrapper> */}
					<BorderWrapper padding={0} gap={0}>
						{isAiItemType && (
							<AiModalHandle
								itemId={itemId}
								itemType={itemType}
								maximumScore={maximumScore}
							/>
						)}
						<Box paddingHorizontal={16} paddingBottom={16}>
							{isAiItemType && (
								<AiPostAnswerHeader
									correct={correct}
									numAnsweredStat={
										correct
											? t("session.answer.correct")
											: t("session.answer.incorrect")
									}
									confidence={confidence}
									pointsEarnedStatValue={pointsEarnedStatValue}
									score={score}
									subskillAssessments={subskillAssessments}
									totalScore={maximumScore}
									itemType={itemType}
									originalItemType={originalItemType}
									stimulusType={stimulusType}
									subskillsSchema={subskillsSchema}
									topFiveEmotions={currentItemAiResponse?.topFiveEmotions}
								/>
							)}
							{showExplanation ? (
								<ExplanationContainer
									itemId={Number(itemId)}
									itemType={itemType}
									choices={choices}
								/>
							) : null}
						</Box>
					</BorderWrapper>
				</Box>
			) : null}
		</Animatable.View>
	);
};

export default PostAnswerModal;
